import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { useSampleSchedules } from "@/hooks/use-sample-schedules";
import { useScheduleColumns } from "@/components/schedules/schedule-columns";
import {
  ScheduleFilters,
  type ScheduleFilterValues,
} from "@/components/schedules/schedule-filters";
import {
  PageHeader,
  Button,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
  Breadcrumbs,
} from "@/components/ui";

type SampleSchedule = components["schemas"]["SampleSchedule"];

export const Route = createFileRoute("/_authenticated/schedules/")({
  component: ScheduleListPage,
});

const PAGE_SIZE = 20;

function ScheduleListPage() {
  const { t } = useTranslation(["schedules", "common"]);
  const navigate = useNavigate();

  const orgId = useAppStore((s) => s.activeOrgId);

  const [filters, setFilters] = useState<ScheduleFilterValues>({});
  const [page, setPage] = useState(1);

  const columns = useScheduleColumns();

  const offset = (page - 1) * PAGE_SIZE;

  const { data, isLoading, isFetching } = useSampleSchedules(orgId, {
    status: filters.status,
    collection_mode: filters.collection_mode,
    limit: PAGE_SIZE,
    offset,
  });

  const schedules = data?.sample_schedules ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleFilterChange = (newFilters: ScheduleFilterValues) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleRowClick = (row: SampleSchedule) => {
    void navigate({ to: "/schedules/$scheduleId", params: { scheduleId: row.id } });
  };

  return (
    <div className="p-8">
      <Breadcrumbs items={[{ label: t("schedules:title") }]} />

      <PageHeader
        title={t("schedules:title")}
        actions={
          <Button onClick={() => void navigate({ to: "/schedules/new" })}>
            {t("schedules:createSchedule")}
          </Button>
        }
      />

      <div className="mt-6 space-y-4">
        <ScheduleFilters values={filters} onChange={handleFilterChange} />

        {isLoading ? (
          <SkeletonTable columns={6} />
        ) : schedules.length === 0 && !isFetching ? (
          <EmptyState
            title={t("schedules:listEmpty")}
            description={t("schedules:listEmptyAction")}
            actionLabel={t("schedules:createSchedule")}
            onAction={() => void navigate({ to: "/schedules/new" })}
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={schedules}
              onRowClick={handleRowClick}
              isLoading={isFetching}
            />
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
